import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  ChevronRightIcon,
  DownloadIcon,
  MicrophoneIcon,
  UsersIcon,
} from "@/components/Icons";
import GlobalDictationOnboarding from "@/components/GlobalDictationOnboarding";
import { actions, config, useAppStore } from "@/controller";
import { accountEmail, hasAccountIdentity } from "@/auth-identity";
import {
  getAndroidDictationResumeStep,
  getNativeHomeJourney,
  type NativeHomeJourneyStep,
} from "@/dictation-interactions";
import {
  configureGlobalDictationAsync,
  getGlobalDictationStatusAsync,
  isGlobalDictationAvailable,
  type GlobalDictationStatus,
} from "@/models/useGlobalDictation";
import { useAuth } from "@/models/useAuth";
import { getPlatform, getPlatformRuntime } from "@/platform";
import { APP_DOWNLOAD_URLS, openEmailDraft } from "@/product-links";
import { logClientEvent } from "@/telemetry";
import { useUISlice } from "@/models/useUISlice";

type Props = {
  onDictationSurfaceChange?: (open: boolean) => void;
};

// The phone Home's next-step card, shared by Android and iOS. Only how
// dictation gets set up differs: Android walks its permissions (and resumes at
// the missing one), iOS adds the 1Transcribe keyboard.
const NativeHomeJourneyContent = ({ onDictationSurfaceChange }: Props) => {
  const { t } = useTranslation();
  const userToken = useAppStore((state) => state.userToken);
  const connectedClients = useAppStore(
    (state) => state.userData?.connectedClients,
  );
  const authUser = useMemo(
    () => useAuth.currentAuthUser(userToken),
    [userToken],
  );
  const [status, setStatus] = useState<GlobalDictationStatus>();
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const lastIncompleteStep = useRef<NativeHomeJourneyStep | undefined>(
    undefined,
  );

  const refreshStatus = useCallback(async () => {
    try {
      setStatus(
        await configureGlobalDictationAsync({
          authToken: userToken || "",
          serverUrl: config.apiUrl,
        }),
      );
    } catch (error) {
      console.error("[GlobalDictation] configure failed", error);
      setStatus(await getGlobalDictationStatusAsync());
    }
  }, [userToken]);

  useEffect(() => {
    void refreshStatus();
    let resumeListener: { remove: () => Promise<void> } | undefined;
    void getPlatformRuntime()
      .app.onResume(async () => {
        await actions.user.get();
        await refreshStatus();
      })
      .then((listener) => {
        resumeListener = listener;
      });
    return () => {
      void resumeListener?.remove();
    };
  }, [refreshStatus]);

  useEffect(() => {
    onDictationSurfaceChange?.(onboardingOpen);
  }, [onboardingOpen, onDictationSurfaceChange]);

  // The native overlay brings the app forward with this one-shot flag when
  // the user chooses its upgrade action. Keep consuming it after the Home
  // promotion itself is gone.
  useEffect(() => {
    if (!status?.paywallRequired) return;
    logClientEvent("dictation.paywallRequired");
    useUISlice
      .getState()
      .setPaywallVisible({ utmSource: "global-dictation --> bubble" });
  }, [status?.paywallRequired]);

  const journey = useMemo(
    () =>
      getNativeHomeJourney({
        dictationSetupComplete: Boolean(status?.setupComplete),
        hasAccount: hasAccountIdentity(authUser),
        connectedClients,
      }),
    [authUser, connectedClients, status],
  );

  useEffect(() => {
    if (!status) return;
    if (journey.nextStep) {
      const completedStep = lastIncompleteStep.current;
      if (completedStep && completedStep !== journey.nextStep) {
        logClientEvent("activationJourney.stepCompleted", {
          step: completedStep,
          count: journey.completedCount,
          features: [...journey.completedSteps],
        });
      }
      lastIncompleteStep.current = journey.nextStep;
      logClientEvent("activationJourney.stepViewed", {
        step: journey.nextStep,
        count: journey.completedCount,
        features: [...journey.completedSteps],
      });
      return;
    }
    if (!lastIncompleteStep.current) return;
    logClientEvent("activationJourney.stepCompleted", {
      step: lastIncompleteStep.current,
      count: journey.completedCount,
      features: [...journey.completedSteps],
    });
    lastIncompleteStep.current = undefined;
    logClientEvent("activationJourney.completed", {
      count: journey.completedCount,
      features: [...journey.completedSteps],
    });
  }, [journey, status]);

  const openNextStep = () => {
    const step = journey.nextStep;
    if (!step) return;
    logClientEvent("activationJourney.stepSelected", {
      step,
      count: journey.completedCount,
      features: [...journey.completedSteps],
    });

    if (step === "dictation") {
      logClientEvent("dictation.onboardingOpened", {
        source: "home-journey",
      });
      setOnboardingOpen(true);
      return;
    }
    if (step === "account") {
      useAuth.signIn({ callback: () => {} });
      return;
    }

    const recipient = accountEmail(authUser);
    if (!recipient) {
      useAuth.signIn({ callback: () => {} });
      return;
    }
    logClientEvent("dictation.desktopCrossSellSelected", {
      source: "home-journey",
      destination: "directDownloads",
      method: "emailDraft",
    });
    try {
      openEmailDraft({
        to: recipient,
        subject: t("settings.computerDownloadEmailSubject", {
          defaultValue: "Install 1Transcribe on your computer",
        }),
        body: t("settings.computerDownloadEmailBody", {
          defaultValue:
            "Open this email on your computer and choose a download:\n\nWindows:\n{{windowsUrl}}\n\nmacOS:\n{{macUrl}}\n\nAfter installing, sign in to 1Transcribe with:\n{{accountEmail}}\n\nYour transcripts and subscription will stay connected.",
          windowsUrl: APP_DOWNLOAD_URLS.windows,
          macUrl: APP_DOWNLOAD_URLS.mac,
          accountEmail: recipient,
        }),
      });
    } catch {
      // A missing mail client must not disrupt Home.
    }
  };

  const dictationResumeStep =
    getPlatform() === "android"
      ? getAndroidDictationResumeStep(status)
      : undefined;
  const dictationResumeSubtitle =
    dictationResumeStep === "overlay"
      ? t("globalDictationOnboarding.resume.overlay", {
          defaultValue: "Continue setup · Display over other apps",
        })
      : dictationResumeStep === "accessibility"
        ? t("globalDictationOnboarding.resume.accessibility", {
            defaultValue: "1 permission left · Accessibility",
          })
        : dictationResumeStep === "done"
          ? t("globalDictationOnboarding.resume.done", {
              defaultValue: "Permissions ready · Turn on dictation",
            })
          : t("settings.setUpDictationSubtitle");

  const stepContent = {
    dictation: {
      title: t("settings.setUpDictation"),
      subtitle: dictationResumeSubtitle,
      icon: MicrophoneIcon,
    },
    account: {
      title: t("settings.signInToSync"),
      subtitle: t("settings.signInSubtitle"),
      icon: UsersIcon,
    },
    desktop: {
      title: t("settings.useOnComputer", {
        defaultValue: "Use on your computer",
      }),
      subtitle: t("settings.dictationOnComputer", {
        defaultValue: "Dictation on Mac & Windows",
      }),
      icon: DownloadIcon,
    },
  } satisfies Record<
    NativeHomeJourneyStep,
    {
      title: string;
      subtitle: string;
      icon: typeof MicrophoneIcon;
    }
  >;

  const closeOnboarding = () => {
    setOnboardingOpen(false);
    void refreshStatus();
  };
  const onboardingLayer = createPortal(
    <GlobalDictationOnboarding
      open={onboardingOpen}
      initialStep={dictationResumeStep}
      onClose={closeOnboarding}
    />,
    document.body,
  );

  if (!status || journey.isComplete || !journey.nextStep) {
    return onboardingLayer;
  }

  const next = stepContent[journey.nextStep];
  const NextIcon = next.icon;

  return (
    <>
      <section className="mb-3" aria-label={next.title}>
        <button
          type="button"
          data-action-id="home.journey.resume"
          onClick={openNextStep}
          className="glass-tile flex min-h-16 w-full items-center gap-3 rounded-2xl px-3.5 py-2.5 text-start transition-colors active:bg-[var(--glass-bg-active)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--accent)]/10 text-[var(--accent)]">
            <NextIcon size={20} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[15px] font-semibold text-[var(--text-primary)]">
              {next.title}
            </span>
            <span className="mt-0.5 block truncate text-xs text-[var(--text-secondary)]">
              {next.subtitle}
            </span>
          </span>
          <ChevronRightIcon
            size={18}
            className="shrink-0 text-[var(--text-tertiary)] rtl:rotate-180"
          />
        </button>
      </section>

      {onboardingLayer}
    </>
  );
};

const NativeHomeJourney = (props: Props) =>
  isGlobalDictationAvailable() ? <NativeHomeJourneyContent {...props} /> : null;

export default NativeHomeJourney;
